
(() => {
  'use strict';

  const stringsNode = document.getElementById('ThemeStrings');
  let strings = {};
  try {
    strings = JSON.parse(stringsNode?.textContent || '{}');
  } catch {}

  const label = (key, product = '') =>
    String(strings[key] || key).replaceAll('__PRODUCT__', product);

  const root = window.Shopify?.routes?.root || '/';
  const dialog = document.getElementById('CartDrawer');

  let cartBusy = false;
  let currency = 'USD';
  let returnFocus = null;

  const money = value =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(value / 100);

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const safeUrl = url => {
    if (typeof url !== 'string' || !url.trim()) return '';
    try {
      const parsed = new URL(url, location.origin);
      return ['https:', 'http:'].includes(parsed.protocol) ? parsed.href : '';
    } catch {
      return '';
    }
  };

  const nativeFetch =
    typeof window.fetch === 'function'
      ? window.fetch.bind(window)
      : (...args) => fetch(...args);

  // Allow cart apps to observe theme requests.
  let requestingFromTheme = false;

  async function request(path, options = {}) {
    let responsePromise;
    requestingFromTheme = true;

    try {
      responsePromise = window.fetch(root + path, {
        credentials: 'same-origin',
        ...options,
        headers: {
          Accept: 'application/json',
          ...options.headers
        }
      });
    } finally {
      requestingFromTheme = false;
    }

    const response = await responsePromise;
    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        label('cart.error')
      );
    }

    return result;
  }

  function setBusy(busy) {
    cartBusy = busy;

    dialog?.querySelectorAll('button').forEach(button => {
      if (!button.hasAttribute('data-close-cart')) {
        button.disabled = busy;
      }
    });
  }

  function cartMessage(message) {
    const node = dialog?.querySelector('[data-cart-message]');
    if (node) node.textContent = message;
  }

  function cartButton(symbol, buttonLabel, callback) {
    const button = create('button', 'icon-button', symbol);
    button.type = 'button';
    button.setAttribute('aria-label', buttonLabel);
    button.addEventListener('click', callback);
    return button;
  }

  function renderCart(cart) {
    if (!dialog) return;

    currency = cart.currency || 'USD';

    document.querySelectorAll('[data-cart-count]').forEach(node => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });

    const items = dialog.querySelector('[data-cart-items]');

    // Preserve elements inserted by third-party apps.
    const oldRows = Array.from(items.children).filter(
      node => node.dataset.elavireCartRow === 'true'
    );

    const insertBefore = oldRows.length
      ? oldRows[oldRows.length - 1].nextSibling
      : items.firstChild;

    oldRows.forEach(node => node.remove());

    const rows = document.createDocumentFragment();

    if (cart.item_count === 0) {
      const empty = create('p', 'muted', label('cart.empty'));
      empty.dataset.elavireCartRow = 'true';
      rows.append(empty);
    }

    cart.items.forEach(item => {
      const row = create('article', 'cart-item');
      row.dataset.elavireCartRow = 'true';

      const imageUrl = safeUrl(
        item.image || item.featured_image?.url || ''
      );

      if (imageUrl) {
        const image = create('img');
        image.src = imageUrl;
        image.alt = item.product_title;
        image.width = 80;
        image.height = 90;
        image.addEventListener('error', () => {
          image.hidden = true;
        });
        row.append(image);
      } else {
        row.append(create('div'));
      }

      const info = create('div');
      const link = create('a');
      link.href = safeUrl(item.url);
      link.append(create('h3', '', item.product_title));
      info.append(link);

      if (
        item.variant_title &&
        item.variant_title !== 'Default Title'
      ) {
        info.append(create('p', '', item.variant_title));
      }

      if (item.selling_plan_allocation?.selling_plan?.name) {
        info.append(
          create(
            'p',
            'cart-meta',
            item.selling_plan_allocation.selling_plan.name
          )
        );
      }

      Object.entries(item.properties || {}).forEach(([name, value]) => {
        if (
          !name ||
          name.startsWith('_') ||
          value === null ||
          value === '' ||
          typeof value === 'object'
        ) return;

        info.append(
          create('p', 'cart-meta', name + ': ' + value)
        );
      });

      const price = create('p');

      if (item.original_line_price > item.final_line_price) {
        price.append(
          create(
            'del',
            'product-compare-price',
            money(item.original_line_price)
          ),
          ' '
        );
      }

      price.append(money(item.final_line_price));
      info.append(price);

      if (item.line_level_discount_allocations?.length) {
        info.append(
          create(
            'p',
            'cart-meta',
            item.line_level_discount_allocations
              .map(d => d.discount_application.title)
              .join(', ')
          )
        );
      }

      const controls = create('div', 'cart-item-controls');

      controls.append(
        cartButton(
          '−',
          label('cart.decrease', item.product_title),
          () => changeQuantity(
            item.key,
            Math.max(0, item.quantity - 1)
          )
        )
      );

      controls.append(
        create('span', '', String(item.quantity))
      );

      controls.append(
        cartButton(
          '+',
          label('cart.increase', item.product_title),
          () => changeQuantity(item.key, item.quantity + 1)
        )
      );

      controls.append(
        cartButton(
          '×',
          label('cart.remove', item.product_title),
          () => changeQuantity(item.key, 0)
        )
      );

      info.append(controls);
      row.append(info);
      rows.append(row);
    });

    items.insertBefore(rows, insertBefore);

    dialog.querySelector('[data-cart-total]').textContent =
      money(cart.total_price);

    const checkout = dialog.querySelector('[data-checkout]');
    checkout.disabled = cart.item_count === 0 || cartBusy;
  }

  function notifyCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart }
      })
    );
  }

  // Free gift configuration.
  const giftHandle = 'elavire-anti-wrinkle-serum';
  const giftProperty = '_elavire_complimentary_serum';

  const qualifies = item =>
    /age[- ]booster/i.test(
      String(item.handle || item.product_title || '')
    );

  const promotionalGift = item =>
    item.handle === giftHandle &&
    item.properties?.[giftProperty] === 'yes';

  // Remove promotional serum if the AGE Booster is removed.
  async function removeOrphanGift(cart) {
    if (!cart?.items || cart.items.some(qualifies)) {
      return cart;
    }

    const orphan = cart.items.find(promotionalGift);
    if (!orphan) return cart;

    await request('cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: orphan.key,
        quantity: 0
      })
    });

    return request('cart.js');
  }

  async function refreshCart() {
    let cart = await request('cart.js');
    cart = await removeOrphanGift(cart);
    renderCart(cart);
    notifyCartUpdated(cart);
    return cart;
  }

  async function openCart() {
    if (!dialog) {
      location.assign(root + 'cart');
      return;
    }

    if (!dialog.open) {
      returnFocus = document.activeElement;
      dialog.showModal();
    }

    cartMessage(label('cart.loading'));

    try {
      await refreshCart();
      cartMessage('');
    } catch (error) {
      cartMessage(error.message);

      const checkout = dialog.querySelector('[data-checkout]');
      if (checkout) checkout.disabled = true;
    }
  }

  async function changeQuantity(id, quantity) {
    if (cartBusy) return;

    setBusy(true);
    cartMessage('');

    try {
      let cart = await request('cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, quantity })
      });

      cart = await removeOrphanGift(cart);

      setBusy(false);
      renderCart(cart);
      notifyCartUpdated(cart);
    } catch (error) {
      setBusy(false);
      cartMessage(error.message);

      try {
        await refreshCart();
      } catch {}
    }
  }

  document.addEventListener('click', event => {
    if (event.target.closest('[data-open-cart]')) {
      event.preventDefault();
      openCart();
    }

    if (event.target.closest('[data-close-cart]')) {
      dialog?.close();
    }

    const gallery = event.target.closest('[data-product-gallery]');

    if (gallery) {
      const thumbnails = Array.from(
        gallery.querySelectorAll('[data-gallery-thumbnail]')
      );

      const main = gallery.querySelector('[data-gallery-main]');

      if (!main || thumbnails.length === 0) return;

      const activeIndex = Math.max(
        0,
        thumbnails.findIndex(
          node => node.getAttribute('aria-pressed') === 'true'
        )
      );

      const clicked = event.target.closest(
        '[data-gallery-thumbnail]'
      );

      let nextIndex = clicked
        ? thumbnails.indexOf(clicked)
        : activeIndex;

      if (event.target.closest('[data-gallery-next]')) {
        nextIndex = (activeIndex + 1) % thumbnails.length;
      } else if (event.target.closest('[data-gallery-previous]')) {
        nextIndex =
          (activeIndex - 1 + thumbnails.length) %
          thumbnails.length;
      } else if (!clicked) {
        return;
      }

      const next = thumbnails[nextIndex];
      const url = safeUrl(next.dataset.photoUrl);

      if (!url) return;

      main.src = url;
      main.alt = next.dataset.photoAlt || '';

      main.classList.toggle(
        'contain',
        next.dataset.photoContain === 'true'
      );

      thumbnails.forEach((node, index) => {
        node.setAttribute(
          'aria-pressed',
          String(index === nextIndex)
        );
      });

      next.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'instant'
      });
    }
  });

  dialog?.addEventListener('close', () => {
    returnFocus?.focus();
  });

  dialog?.addEventListener('click', event => {
    if (event.target === dialog) {
      const r = dialog.getBoundingClientRect();

      if (
        event.clientX < r.left ||
        event.clientX > r.right
      ) {
        dialog.close();
      }
    }
  });

  // Add to cart with an optional free serum.
  document.addEventListener('submit', async event => {
    const form = event.target;

    if (
      !form.matches('[data-product-form]') ||
      event.defaultPrevented
    ) return;

    event.preventDefault();

    const button = form.querySelector('[data-add-button]');

    if (!button || button.disabled) return;

    const previousLabel = button.textContent;
    const message = form.querySelector('[data-form-message]');

    button.disabled = true;
    button.textContent = label('product.adding');

    if (message) message.textContent = '';

    try {
      const giftBox = form
        .closest('.elavire-block-buy_buttons')
        ?.querySelector('[data-elavire-gift-variant]');

      // Only add serum when checkbox is checked.
      const giftSelected = Boolean(
        giftBox
          ?.querySelector('[data-elavire-gift-opt-in]')
          ?.checked
      );

      if (giftBox && giftSelected) {
        const giftVariantId = Number(
          giftBox.dataset.elavireGiftVariant
        );

        const formData = new FormData(form);
        const mainVariantId = Number(formData.get('id'));
        const quantity = Number(
          formData.get('quantity') || 1
        );

        if (
          !Number.isSafeInteger(giftVariantId) ||
          !Number.isSafeInteger(mainVariantId) ||
          !Number.isSafeInteger(quantity) ||
          quantity < 1
        ) {
          throw new Error(
            'Unable to add the product and gift. Please reload and try again.'
          );
        }

        const mainItem = {
          id: mainVariantId,
          quantity
        };

        const props = {};

        for (const [key, value] of formData.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);

          if (match && typeof value === 'string') {
            props[match[1]] = value;
          }
        }

        if (Object.keys(props).length) {
          mainItem.properties = props;
        }

        const sellingPlan = formData.get('selling_plan');

        if (sellingPlan) {
          mainItem.selling_plan =
            Number(sellingPlan) || sellingPlan;
        }

        const existing = await request('cart.js');

        const hasGift = existing.items.some(
          item => item.handle === giftHandle
        );

        const items = [mainItem];

        // Add only one promotional serum.
        if (!hasGift) {
          items.push({
            id: giftVariantId,
            quantity: 1,
            properties: {
              [giftProperty]: 'yes'
            }
          });
        }

        await request('cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });
      } else {
        // Checkbox unchecked: add only main product.
        await request('cart/add.js', {
          method: 'POST',
          body: new FormData(form)
        });
      }

      if (message) {
        message.textContent = label('product.added');
      }

      await openCart();
    } catch (error) {
      if (message) {
        message.textContent = error.message;
      }
    } finally {
      button.textContent = previousLabel;
      button.disabled = false;
    }
  });

  document.addEventListener('change', event => {
    const select = event.target.closest('[data-variant-select]');

    if (!select) return;

    const option = select.selectedOptions[0];

    if (!option) return;

    const available = option.dataset.available === 'true';

    document.querySelectorAll('[data-product-form]').forEach(form => {
      const id = form.querySelector('[name="id"]');

      if (id) id.value = select.value;
    });

    document
      .querySelectorAll(
        'form[action*="/cart/add"]:not([data-product-form]) input[name="id"]'
      )
      .forEach(input => {
        input.value = select.value;
      });

    document.querySelectorAll('[data-product-form]').forEach(form => {
      const button = form.querySelector('[data-add-button]');

      if (button) {
        button.disabled = !available;
        button.textContent = available
          ? label('product.add')
          : label('product.sold_out');
      }
    });

    document.querySelectorAll('[data-product-price]').forEach(node => {
      node.textContent = option.dataset.price;
    });

    document
      .querySelectorAll('[data-product-compare-price]')
      .forEach(node => {
        node.textContent = option.dataset.comparePrice || '';
        node.hidden = !option.dataset.comparePrice;
      });

    const url = new URL(location.href);
    url.searchParams.set('variant', select.value);
    history.replaceState({}, '', url);

    document.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: {
          variantId: Number(select.value)
        }
      })
    );

    const variants = document.querySelector(
      '[data-product-variants]'
    );

    try {
      const variant = JSON.parse(
        variants?.textContent || '[]'
      ).find(v => String(v.id) === select.value);

      if (variant?.quantity_rule) {
        document
          .querySelectorAll(
            '[data-product-form] input[name="quantity"]'
          )
          .forEach(input => {
            input.min = variant.quantity_rule.min || 1;
            input.step = variant.quantity_rule.increment || 1;

            if (variant.quantity_rule.max) {
              input.max = variant.quantity_rule.max;
            } else {
              input.removeAttribute('max');
            }

            input.value = variant.quantity_rule.min || 1;
          });
      }
    } catch {}
  });

  // Synchronize the cart with third-party apps.
  const cartMutation =
    /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  let syncTimer = null;

  function syncAfterApp(url) {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      if (
        /\/cart\/add/.test(url) &&
        dialog &&
        !dialog.open
      ) {
        openCart();
      } else {
        refreshCart().catch(() => {});
      }
    }, 150);
  }

  const urlOf = input => {
    try {
      return new URL(
        typeof input === 'string'
          ? input
          : input?.url || String(input),
        location.origin
      ).pathname;
    } catch {
      return '';
    }
  };

  window.fetch = function (input, init) {
    const fromTheme = requestingFromTheme;
    const result = nativeFetch(input, init);
    const path = urlOf(input);

    if (!fromTheme && cartMutation.test(path)) {
      result
        .then(response => {
          if (response.ok) syncAfterApp(path);
        })
        .catch(() => {});
    }

    return result;
  };

  if (window.XMLHttpRequest) {
    const nativeOpen =
      window.XMLHttpRequest.prototype.open;

    window.XMLHttpRequest.prototype.open = function (
      method,
      url,
      ...rest
    ) {
      const path = urlOf(url);

      if (cartMutation.test(path)) {
        this.addEventListener('load', () => {
          if (this.status < 400) {
            syncAfterApp(path);
          }
        });
      }

      return nativeOpen.call(
        this,
        method,
        url,
        ...rest
      );
    };
  }

  [
    'cart:refresh',
    'cart:build',
    'theme:cart:refresh',
    'ajaxProduct:added'
  ].forEach(name => {
    document.addEventListener(
      name,
      () => refreshCart().catch(() => {})
    );
  });

  [
    'cart:open',
    'theme:cart:open',
    'cart-drawer:open'
  ].forEach(name => {
    document.addEventListener(
      name,
      () => openCart()
    );
  });

  window.ElavireCart = {
    open: openCart,
    refresh: refreshCart
  };

  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'visible') {
        refreshCart().catch(() => {});
      }
    }
  );

  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      refreshCart().catch(() => {});
    }
  });
})();
